import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { ReleaseUpdatePublication } from "../../types/ReleaseUpdate";
import {
  consumeShowLatestReleaseUpdateAfterLoginFlag,
  fetchLatestUnreadReleaseUpdatePublication,
  fetchReleaseUpdatePublication,
  getReleaseUpdateArchivePath,
  markReleaseUpdatePublicationAsRead,
} from "../../services/releaseUpdateService";
import { useCurrentUser } from "../../provider/CurrentUserProvider";
import ReleaseUpdateDialog from "./ReleaseUpdateDialog";

interface ReleaseUpdateDialogContextValue {
  openReleaseUpdate: (publicationId: number) => Promise<void>;
  openReleaseUpdatePreview: (publication: ReleaseUpdatePublication) => void;
}

const ReleaseUpdateDialogContext = createContext<ReleaseUpdateDialogContextValue | null>(null);
let pendingLoginPublicationRequest: Promise<ReleaseUpdatePublication | null> | null = null;

const getLoginPublicationRequest = (backofficeUserIri: string) => {
  if (consumeShowLatestReleaseUpdateAfterLoginFlag()) {
    const request = fetchLatestUnreadReleaseUpdatePublication(backofficeUserIri);
    pendingLoginPublicationRequest = request;
    const clearRequest = () => {
      if (pendingLoginPublicationRequest === request) {
        pendingLoginPublicationRequest = null;
      }
    };
    request.then(clearRequest, clearRequest);
  }

  return pendingLoginPublicationRequest;
};

type ReleaseUpdateDialogProviderProps = {
  children: ReactNode;
};

type ReleaseUpdateDialogState = {
  publication: ReleaseUpdatePublication;
  isPreview: boolean;
} | null;

export default function ReleaseUpdateDialogProvider({ children }: ReleaseUpdateDialogProviderProps) {
  const [dialog, setDialog] = useState<ReleaseUpdateDialogState>(null);
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const showLoadError = () => {
    enqueueSnackbar("Das Release-Update konnte nicht geladen werden.", { variant: "error" });
  };

  const openReleaseUpdate = async (publicationId: number) => {
    try {
      const loadedPublication = await fetchReleaseUpdatePublication(publicationId);
      setDialog({ publication: loadedPublication, isPreview: false });
    } catch (error) {
      showLoadError();
    }
  };

  const openReleaseUpdatePreview = (previewPublication: ReleaseUpdatePublication) => {
    setDialog({ publication: previewPublication, isPreview: true });
  };

  useEffect(() => {
    // This mount-bound check intentionally consumes the one-shot flag set only by the credential login form.
    const request = getLoginPublicationRequest(currentUser["@id"]);
    if (!request) {
      return;
    }

    const showLoginPublication = async () => {
      try {
        const latestPublication = await request;
        if (latestPublication) {
          setDialog({ publication: latestPublication, isPreview: false });
        }
      } catch (error) {
        showLoadError();
      }
    };

    showLoginPublication();
  }, []);

  const dismissDialog = async () => {
    if (!dialog) {
      return;
    }
    if (!dialog.isPreview) {
      try {
        await markReleaseUpdatePublicationAsRead(dialog.publication.id, currentUser["@id"]);
      } catch (error) {
        enqueueSnackbar("Der Lesestatus konnte nicht gespeichert werden.", { variant: "error" });
      }
    }
    setDialog(null);
  };

  const openArchive = async () => {
    if (!dialog) {
      return;
    }

    await dismissDialog();
    navigate(getReleaseUpdateArchivePath(currentUser.isExternal));
  };

  return (
    <ReleaseUpdateDialogContext.Provider value={{ openReleaseUpdate, openReleaseUpdatePreview }}>
      {children}
      {dialog && (
        <ReleaseUpdateDialog
          key={`${dialog.isPreview ? "preview" : "publication"}-${dialog.publication.id}`}
          publication={dialog.publication}
          isPreview={dialog.isPreview}
          onClose={dismissDialog}
          onOpenArchive={openArchive}
        />
      )}
    </ReleaseUpdateDialogContext.Provider>
  );
}

export const useReleaseUpdateDialog = () => {
  const context = useContext(ReleaseUpdateDialogContext);
  if (!context) {
    throw new Error("useReleaseUpdateDialog must be used inside ReleaseUpdateDialogProvider");
  }
  return context;
};
